import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import FollowButton from '../components/FollowButton';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

const CONFIGS = {
  followers: { title: 'Followers', fetchUrl: (id) => `/users/${id}/followers`, type: 'users' },
  following: { title: 'Following', fetchUrl: (id) => `/users/${id}/following`, type: 'users' },
  hosted: { title: 'Hosted Plans', fetchUrl: (id) => `/users/${id}/hosted-plans`, type: 'plans' },
  attended: { title: 'Attended Plans', fetchUrl: (id) => `/users/${id}/attended-plans`, type: 'plans' },
};

export default function StatsList() {
  const { id: userId, type } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const config = CONFIGS[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!config) { navigate(-1); return; }
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(config.fetchUrl(userId));
        setItems(data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [userId, type, config, navigate]);

  const filtered = query
    ? items.filter(item => {
        const name = item.display_name || item.title || '';
        const username = item.username || '';
        const q = query.toLowerCase();
        return name.toLowerCase().includes(q) || username.toLowerCase().includes(q);
      })
    : items;

  if (!config) return null;

  return (
    <div className="content-area animate-fade-in" data-testid={`stats-${type}-page`}>
      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="stats-back">
          <ArrowLeft size={22} className="text-[#111111]" />
        </button>
        <h1 className="text-xl font-bold text-[#111111]">{config.title}</h1>
        <span className="text-sm text-[#9B9EA3]">{items.length}</span>
      </div>

      {/* Search */}
      <div className="px-4 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9B9EA3]" />
          <input
            data-testid="stats-search"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={config.type === 'users' ? 'Search by name or username...' : 'Search plans...'}
            className="w-full bg-white text-[#111111] rounded-2xl pl-10 pr-10 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8]"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2">
              <X size={16} className="text-[#9B9EA3]" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-[#9B9EA3]">
              {query ? 'No results found' : `No ${config.title.toLowerCase()} yet`}
            </p>
          </div>
        ) : config.type === 'users' ? (
          <div className="space-y-1.5">
            {filtered.map(u => {
              const isSelf = currentUser?.id === u.id;
              return (
                <div
                  key={u.id}
                  data-testid={`stat-user-${u.id}`}
                  onClick={() => navigate(`/user/${u.id}`)}
                  className="flex items-center gap-3 p-3 rounded-2xl active:bg-[#F1F0EA] transition-all cursor-pointer"
                >
                  {u.profile_photo ? (
                    <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3] flex-shrink-0">
                      {getInitials(u.display_name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#111111] truncate">{u.display_name}</p>
                    <p className="text-xs text-[#9B9EA3]">@{u.username}</p>
                    {u.bio && <p className="text-xs text-[#6B6E73] mt-0.5 truncate">{u.bio}</p>}
                  </div>
                  {!isSelf && (
                    <FollowButton
                      user={u}
                      testIdPrefix={`stat-${u.id}`}
                      onChange={(newStatus) => {
                        setItems(prev => prev.map(p => p.id === u.id ? { ...p, is_following: newStatus } : p));
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(p => (
              <div
                key={p.id}
                data-testid={`stat-plan-${p.id}`}
                onClick={() => navigate(`/plan/${p.id}`)}
                className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-black/[0.06] shadow-sm active:scale-[0.99] transition-all cursor-pointer"
              >
                {p.cover_image ? (
                  <img loading="lazy" decoding="async" src={p.cover_image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3] flex-shrink-0">
                    {(p.title || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#111111] truncate">{p.title}</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">{p.date} {p.start_time}</p>
                  <p className="text-xs text-[#6B6E73] truncate">{p.venue || p.city}</p>
                </div>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${
                  p.status === 'active' ? 'bg-[#B6E600]/15 text-[#3F5200]' :
                  p.status === 'completed' ? 'bg-[#F1F0EA] text-[#9B9EA3]' :
                  'bg-red-50 text-red-500'
                }`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
